import React, { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, Mail, Phone } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { useDepartments } from "@/context/DepartmentsContext";

/**
 * Contact page — architectural / industrial rigor.
 * Grid-driven layout, black hairline dividers, mono labels, generous negative space.
 * Focus: data transparency for partners (identification, ISO, addresses, banking).
 */
export default function ContactPage() {
  const { t } = useSite();
  const { departments } = useDepartments();
  const [form, setForm] = useState({ name: "", email: "", phone: "", department: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const change = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      setForm({ name: "", email: "", phone: "", department: "", message: "" });
      toast.success("Mesajul a fost trimis.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const input =
    "w-full bg-white border border-black rounded-none px-4 py-3 focus:outline-none focus:ring-0 focus:border-black placeholder:text-black/40";

  return (
    <div data-testid="contact-page">
      {/* HERO */}
      <section className="border-b border-black">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 lg:px-14 py-16 md:py-24">
          <div className="font-mono-label text-black/60">{t("section.contact.hero.eyebrow")}</div>
          <h1 className="mt-4 font-display font-medium uppercase text-5xl md:text-8xl tracking-tighter leading-[0.9]">
            {t("contact.hero.title_1")}
            <br />
            <span className="italic font-normal">{t("contact.hero.title_2")}</span>
          </h1>
        </div>
      </section>

      {/* CONTACT BLOCKS — SUNTEC + BARELLO */}
      <section className="border-b border-black">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 lg:px-14 py-14 md:py-20">
          <div className="font-mono-label text-black/60 mb-8">[ 01 / CONTACT DIRECT ]</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-black border border-black" data-testid="contact-groups">
            {/* SUNTEC */}
            <div className="bg-white p-8 md:p-10" data-testid="contact-block-suntec">
              <div className="font-mono-label text-black/60">Suntec</div>
              <img
                src="/logo-suntec.png"
                alt="Suntec"
                className="mt-2 h-[50px] md:h-[60px] w-auto object-contain"
              />
              <div className="mt-6 space-y-5">
                {[
                  { email: "sales@suntec.ro", phone: "+40 741 111 887" },
                  { email: "contabilitate@suntec.ro", phone: "+40 746 111 531" },
                  { email: "service@suntec.ro" },
                ].map((row) => (
                  <div key={row.email} className="pt-4 border-t border-black/15 first:border-t-0 first:pt-0">
                    <a href={`mailto:${row.email}`} className="flex items-center gap-2 font-display font-bold text-base hover:underline break-all">
                      <Mail size={16} className="shrink-0" /> {row.email}
                    </a>
                    {row.phone && (
                      <a href={`tel:${row.phone.replace(/\s/g, "")}`} className="mt-1 flex items-center gap-2 text-black/80 hover:text-black text-sm">
                        <Phone size={14} /> {row.phone}
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* BARELLO */}
            <div className="bg-white p-8 md:p-10" data-testid="contact-block-barello">
              <div className="font-mono-label text-black/60">Barello | HoReCa</div>
              <img
                src="/logo-barello.png"
                alt="Barello Professional HoReCa Systems"
                className="mt-2 h-[50px] md:h-[60px] w-auto object-contain"
              />
              <div className="mt-6 space-y-5">
                {[
                  { email: "sales@barello.ro", phone: "+40 744 753 853" },
                  { email: "purchasing@barello.ro", phone: "+40 746 111 529" },
                  { email: "service@barello.ro" },
                ].map((row) => (
                  <div key={row.email} className="pt-4 border-t border-black/15 first:border-t-0 first:pt-0">
                    <a href={`mailto:${row.email}`} className="flex items-center gap-2 font-display font-bold text-base hover:underline break-all">
                      <Mail size={16} className="shrink-0" /> {row.email}
                    </a>
                    {row.phone && (
                      <a href={`tel:${row.phone.replace(/\s/g, "")}`} className="mt-1 flex items-center gap-2 text-black/80 hover:text-black text-sm">
                        <Phone size={14} /> {row.phone}
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FORM */}
      <section className="border-b border-black">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 lg:px-14 py-14 md:py-20 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <div className="font-mono-label text-black/60">[ 02 / TRIMITE UN MESAJ ]</div>
            <h2 className="mt-4 font-display font-medium uppercase text-3xl md:text-5xl tracking-tighter leading-[0.95]">
              Discută cu <span className="italic font-normal">specialistul</span> potrivit.
            </h2>
            <p className="mt-6 text-black/70 text-sm leading-relaxed">
              Alege departamentul relevant și îți răspundem în cel mai scurt timp posibil. Formular direct, fără intermediari.
            </p>
          </div>

          <div className="lg:col-span-8 border border-black p-8 md:p-12" data-testid="contact-form-container">
            {sent ? (
              <div data-testid="contact-success">
                <div className="font-mono-label text-black/60">Confirmare</div>
                <div className="mt-3 font-display font-medium uppercase text-3xl">Mesaj trimis cu succes.</div>
                <p className="mt-4 text-black/70">Vă vom contacta în cel mai scurt timp posibil.</p>
                <button
                  onClick={() => setSent(false)}
                  data-testid="send-another-btn"
                  className="mt-8 font-mono-label border border-black px-6 py-3 hover:bg-black hover:text-white"
                >
                  Trimite un alt mesaj
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
                <div>
                  <label className="font-mono-label text-black/60 block mb-2">Nume complet *</label>
                  <input required data-testid="contact-name-input" className={input} value={form.name} onChange={(e) => change("name", e.target.value)} placeholder="Numele dvs." />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="font-mono-label text-black/60 block mb-2">Email *</label>
                    <input required type="email" data-testid="contact-email-input" className={input} value={form.email} onChange={(e) => change("email", e.target.value)} placeholder="nume@companie.ro" />
                  </div>
                  <div>
                    <label className="font-mono-label text-black/60 block mb-2">Telefon</label>
                    <input data-testid="contact-phone-input" className={input} value={form.phone} onChange={(e) => change("phone", e.target.value)} placeholder="+40 ..." />
                  </div>
                </div>
                <div>
                  <label className="font-mono-label text-black/60 block mb-2">Departament</label>
                  <select data-testid="contact-department-select" className={input} value={form.department} onChange={(e) => change("department", e.target.value)}>
                    <option value="">Selectează un departament</option>
                    {(departments || []).map((d) => (
                      <option key={d.slug} value={d.slug}>{d.number} · {d.name}</option>
                    ))}
                    <option value="general">General / Nu sunt sigur</option>
                  </select>
                </div>
                <div>
                  <label className="font-mono-label text-black/60 block mb-2">Mesaj *</label>
                  <textarea required rows={6} data-testid="contact-message-input" className={input} value={form.message} onChange={(e) => change("message", e.target.value)} placeholder="Descrieți solicitarea..." />
                </div>
                <button type="submit" disabled={submitting} data-testid="contact-submit-btn" className="font-mono-label bg-black text-white px-8 py-4 hover:bg-white hover:text-black border border-black inline-flex items-center gap-3 disabled:opacity-50">
                  {submitting ? "Se trimite..." : t("btn.contact.submit.label")} <ArrowRight size={16} />
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* DATE FIRMĂ — architectural grid, corporate transparency */}
      <section className="bg-white" data-testid="contact-company-data">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 lg:px-14 py-20 md:py-28">
          {/* Section header */}
          <div className="pb-12 border-b border-black">
            <div className="font-mono-label text-black/60">[ 03 / DATE OFICIALE ]</div>
            <div className="mt-4 font-mono text-xs uppercase tracking-widest text-black/50">Corporate transparency</div>
          </div>

          {/* 2x2 architectural grid with hairline black dividers */}
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-px bg-black border border-black" data-testid="company-grid">
            {/* IDENTIFICARE */}
            <div className="bg-white p-8 md:p-12" data-testid="company-identification">
              <div className="flex items-baseline justify-between">
                <div className="font-mono-label text-black/60">Identificare</div>
                <div className="font-mono text-xs text-black/40">01</div>
              </div>
              <dl className="mt-8 grid grid-cols-1 gap-y-6">
                {[
                  ["Denumire", "SC SUNTEC TRADE SRL"],
                  ["CUI", "RO18442684"],
                  ["Reg. Com.", "J2006000426059"],
                  ["EUID", "ROONRC.J2006000426059"],
                  ["D-U-N-S", "56-677-8238"],
                  ["Capital social", "200.000 RON"],
                ].map(([k, v]) => (
                  <div key={k} className="grid grid-cols-1 md:grid-cols-5 gap-2 pb-4 border-b border-black/10 last:border-b-0 last:pb-0">
                    <dt className="md:col-span-2 font-mono-label text-black/50 uppercase">{k}</dt>
                    <dd className="md:col-span-3 font-display font-bold text-base tracking-tight break-all">{v}</dd>
                  </div>
                ))}
              </dl>
            </div>

            {/* CERTIFICĂRI ISO */}
            <div className="bg-white p-8 md:p-12" data-testid="company-iso">
              <div className="flex items-baseline justify-between">
                <div className="font-mono-label text-black/60">Certificări ISO</div>
                <div className="font-mono text-xs text-black/40">02</div>
              </div>
              <ul className="mt-8 space-y-6">
                {[
                  ["SR EN ISO 9001:2015", "Management al calității", "UIG-1014-EU-1357"],
                  ["SR EN ISO 14001:2015", "Management de mediu", "UIG-1031-EU-769"],
                  ["SR EN ISO 37001:2015", "Anti-mită & conformitate", "UIG-1037-EU-139"],
                  ["SR EN ISO/IEC 27001:2023", "Securitatea informației", "UIG-1027-EU-169"],
                ].map(([title, scope, cert]) => (
                  <li key={cert} className="pb-6 border-b border-black/10 last:border-b-0 last:pb-0">
                    <div className="font-display font-bold text-base tracking-tight">{title}</div>
                    <div className="mt-1 text-sm text-black/60">{scope}</div>
                    <div className="mt-2 font-mono text-xs text-black/70">Nr. certificat · {cert}</div>
                  </li>
                ))}
              </ul>
            </div>

            {/* ADRESE */}
            <div className="bg-white p-8 md:p-12" data-testid="company-addresses">
              <div className="flex items-baseline justify-between">
                <div className="font-mono-label text-black/60">Adrese</div>
                <div className="font-mono text-xs text-black/40">03</div>
              </div>
              <ul className="mt-8 space-y-6">
                {[
                  ["Sediu central", "str. Principală nr. 72", "417362, Cheriu, Bihor"],
                  ["Punct de lucru", "str. Avântului nr. 15", "410520, Oradea, Bihor"],
                  ["Punct de lucru", "Spinuș nr. 228", "417530, Bihor"],
                ].map(([type, line1, line2]) => (
                  <li key={type + line1} className="pb-6 border-b border-black/10 last:border-b-0 last:pb-0">
                    <div className="font-mono-label text-black/50 uppercase">{type}</div>
                    <div className="mt-2 font-display font-bold text-base tracking-tight">{line1}</div>
                    <div className="text-sm text-black/70">{line2}</div>
                  </li>
                ))}
              </ul>
            </div>

            {/* CONTURI BANCARE */}
            <div className="bg-white p-8 md:p-12" data-testid="company-banks">
              <div className="flex items-baseline justify-between">
                <div className="font-mono-label text-black/60">Conturi bancare</div>
                <div className="font-mono text-xs text-black/40">04</div>
              </div>
              <ul className="mt-8 space-y-5">
                {[
                  ["BCR", "RON", "RO71 RNCB 0032 1061 5386 0003"],
                  ["ING", "RON", "RO50 INGB 0000 9999 0252 3563"],
                  ["ING", "EUR", "RO31 INGB 0000 9999 1832 3556"],
                  ["ING", "USD", "RO48 INGB 0000 9999 1834 2068"],
                  ["Trezoreria Oradea", "RON", "RO15 TREZ 0765 069X XX00 4730"],
                ].map(([bank, ccy, iban]) => (
                  <li key={iban} className="pb-5 border-b border-black/10 last:border-b-0 last:pb-0">
                    <div className="flex items-baseline justify-between gap-4">
                      <span className="font-mono-label text-black/50 uppercase">{bank}</span>
                      <span className="font-mono text-xs text-black/50">{ccy}</span>
                    </div>
                    <div className="mt-2 font-mono text-sm md:text-base break-all tracking-tight">{iban}</div>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Footnote strip */}
          <div className="mt-10 pt-6 border-t border-black/15 flex flex-wrap items-center gap-x-8 gap-y-2 font-mono text-xs uppercase tracking-widest text-black/50">
            <span>SC Suntec Trade SRL</span>
            <span>·</span>
            <span>Bihor, România</span>
            <span>·</span>
            <span>Actualizat {new Date().getFullYear()}</span>
          </div>
        </div>
      </section>
    </div>
  );
}
